import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { useToast } from '../hooks/use-toast';
import { FileText, Download, Edit, Trash2, Plus, FileDown, X, ChevronDown, ChevronUp, Eye, CheckSquare, Square, Check, ChevronsUpDown } from 'lucide-react';
import { SendReportButton } from '../components/common/SendReportButton';
import axios from 'axios';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { PREDEFINED_DESIGNATIONS } from '../components/EmployeeForm';
import { OfferDeptCompanyFields } from '../components/offer-letters/OfferDeptCompanyFields';

const TOP_NATIONALITIES = ['Indian', 'Filipino', 'Nepali', 'Pakistani'];

const statusLabel = (s) => (s === 'signed' ? 'Signed' : s === 'refused' ? 'Refused' : 'Pending');

const StatusBadge = ({ status, testid }) => {
  const s = status || 'pending';
  const cls = s === 'signed'
    ? 'bg-green-100 text-green-700 border-green-300'
    : s === 'refused'
      ? 'bg-red-100 text-red-700 border-red-300'
      : 'bg-gray-100 text-gray-600 border-gray-300';
  return <Badge variant="outline" className={cls} data-testid={testid}>{statusLabel(s)}</Badge>;
};
const ALL_NATIONALITIES = [
  'Afghan','Albanian','Algerian','American','Andorran','Angolan','Argentine','Armenian',
  'Australian','Austrian','Azerbaijani','Bahamian','Bahraini','Bangladeshi','Barbadian',
  'Belarusian','Belgian','Belizean','Beninese','Bhutanese','Bolivian','Bosnian','Brazilian',
  'British','Bruneian','Bulgarian','Burkinabe','Burundian','Cambodian','Cameroonian',
  'Canadian','Cape Verdean','Central African','Chadian','Chilean','Chinese','Colombian',
  'Comorian','Congolese','Costa Rican','Croatian','Cuban','Cypriot','Czech','Danish',
  'Djiboutian','Dominican','Dutch','East Timorese','Ecuadorean','Egyptian','Emirati',
  'Equatorial Guinean','Eritrean','Estonian','Ethiopian','Fijian','Filipino','Finnish',
  'French','Gabonese','Gambian','Georgian','German','Ghanaian','Greek','Grenadian',
  'Guatemalan','Guinean','Guyanese','Haitian','Honduran','Hungarian','Icelandic','Indian',
  'Indonesian','Iranian','Iraqi','Irish','Israeli','Italian','Ivorian','Jamaican','Japanese',
  'Jordanian','Kazakh','Kenyan','Korean','Kuwaiti','Kyrgyz','Laotian','Latvian','Lebanese',
  'Liberian','Libyan','Lithuanian','Luxembourgish','Macedonian','Malagasy','Malawian',
  'Malaysian','Maldivian','Malian','Maltese','Mauritanian','Mauritian','Mexican','Moldovan',
  'Mongolian','Montenegrin','Moroccan','Mozambican','Myanmar','Namibian','Nepalese','Nepali',
  'New Zealander','Nicaraguan','Nigerian','Norwegian','Omani','Pakistani','Panamanian',
  'Paraguayan','Peruvian','Polish','Portuguese','Qatari','Romanian','Russian','Rwandan',
  'Saudi','Senegalese','Serbian','Sierra Leonean','Singaporean','Slovak','Slovenian',
  'Somali','South African','Spanish','Sri Lankan','Sudanese','Surinamese','Swedish','Swiss',
  'Syrian','Taiwanese','Tajik','Tanzanian','Thai','Togolese','Trinidadian','Tunisian',
  'Turkish','Turkmen','Ugandan','Ukrainian','Uruguayan','Uzbek','Venezuelan','Vietnamese',
  'Yemeni','Zambian','Zimbabwean'
];

// Country-name aliases so users can search by country, not just the demonym
// (e.g. typing "Burma" still finds "Myanmar"). onSelect always stores the clean value.
const NATIONALITY_ALIASES = {
  'Myanmar': 'Burma Burmese',
  'Filipino': 'Philippines',
  'Dutch': 'Netherlands Holland',
  'Emirati': 'UAE United Arab Emirates',
  'Sri Lankan': 'Sri Lanka Ceylon',
  'Laotian': 'Laos',
  'Bhutanese': 'Bhutan',
  'Maldivian': 'Maldives',
  'Bruneian': 'Brunei',
  'Cambodian': 'Cambodia',
  'Vietnamese': 'Vietnam',
  'Thai': 'Thailand',
  'Nepali': 'Nepal',
  'Nepalese': 'Nepal',
  'Bangladeshi': 'Bangladesh',
  'Pakistani': 'Pakistan',
  'Indonesian': 'Indonesia',
  'Malaysian': 'Malaysia',
  'Singaporean': 'Singapore',
  'Chinese': 'China',
  'Korean': 'Korea South North',
  'Japanese': 'Japan',
  'Taiwanese': 'Taiwan',
  'Kazakh': 'Kazakhstan',
  'Kyrgyz': 'Kyrgyzstan',
  'Tajik': 'Tajikistan',
  'Turkmen': 'Turkmenistan',
  'Uzbek': 'Uzbekistan',
  'Saudi': 'Saudi Arabia KSA',
  'Omani': 'Oman',
  'Qatari': 'Qatar',
  'Kuwaiti': 'Kuwait',
  'Bahraini': 'Bahrain',
  'Jordanian': 'Jordan',
  'Lebanese': 'Lebanon',
  'Syrian': 'Syria',
  'Iraqi': 'Iraq',
  'Iranian': 'Iran Persia',
  'Yemeni': 'Yemen',
  'Egyptian': 'Egypt',
  'British': 'United Kingdom UK England Britain',
  'American': 'United States USA US America',
  'German': 'Germany',
  'French': 'France',
  'Spanish': 'Spain',
  'Portuguese': 'Portugal',
  'Greek': 'Greece',
  'Danish': 'Denmark',
  'Swedish': 'Sweden',
  'Swiss': 'Switzerland',
  'Ethiopian': 'Ethiopia',
  'Ivorian': "Cote d'Ivoire Ivory Coast",
};

const API = process.env.REACT_APP_BACKEND_URL;

const SIGNATORIES = [
  { name: "Joy P Samuel", designation: "Group Finance Director" },
  { name: "Mohamed Saeed Saif Jabr AlSuwaidi", designation: "CEO" },
  { name: "Matthew Haffner", designation: "Managing Partner" },
  { name: "Jubu Thankachen", designation: "HR Manager" },
];

const defaultAnnexureA = {
  position: '',
  term_of_contract: '2 Year Limited Term Contract',
  starting_date: '',
  total_wage: '',  // Auto-calculated from basic + housing + transportation + others
  basic_wage: '',
  housing_allowance: '',
  transportation_allowance: '',
  others: '',
  benefits_in_kind: 'Employment Visa & Insurance',
  ticket_policy: '730',  // "730" for 2 years, "365" for 1 year
  description_of_duties: 'As Per Appendix B of this Contract attached as a separate Document.',
  primary_location: 'Abu Dhabi',
  probation_period: '6 months from the date of joining',
  normal_hours: 'As Per Laws of UAE Company Policy. The Company Policy is subject to change at Management Discretion and this binds the Employee.',
  sick_days: 'As Per Laws of UAE and Company Policy',
  paid_annual_leave: 'As Per Laws of UAE and Company Policy',
  resignation_notice: 'During Probation: 4 weeks; Post Probation: 1 month',
  special_conditions: `1. Company Policy: The Employee will be bound at all times by the Company Policy. Breaching the Company Policy will result in disciplinary action being taken.
2. Early Termination: If the Employee terminates this contract of Employment within the probationary period, and find employment with another Employer, the new Employer will be responsible for paying all costs of employment during such tenure with the Company.
3. Freelancing or additional outside income is STRICTLY NOT PERMITTED without the express written permission from the Managing Director, and will result in instant dismissal without recourse to any remuneration.
4. Solicitation of staff after employment: You are not permitted to solicit any Company staff for a period of at least 2 years from your final date of employment.
5. Business Secrets & Confidentiality: You are strictly required to ensure any information learned, received or obtained in any method whatsoever during your employment remains the property, and/or intellectual property, of the Company at all times. If you breach any confidentiality or divulge any business secrets during or after your employment with the Company, the Company reserves all rights to pursue legal action against you.`,
  additional_conditions: 'NA',
  language_requirement: 'English Only (fluent written & spoken)',
  personal_email: '',
  whatsapp_contact: '',
};

const defaultAnnexureB = {
  non_compete_duration: 'One (1) calendar year from expiry of the contract',
  non_compete_geography: ['Yas Island'],
  type_of_work: '',
};

export default function OfferLetters() {
  const { toast } = useToast();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isViewer = user.role === 'viewer' || user.role === 'report_viewer';
  
  const [letters, setLetters] = useState([]);
  const [geographies, setGeographies] = useState(['Yas Island']);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingLetter, setEditingLetter] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [expandedSection, setExpandedSection] = useState('basic');
  const [newGeography, setNewGeography] = useState('');
  
  // Preview state
  const [previewLetter, setPreviewLetter] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  
  // Bulk selection state
  const [selectedLetters, setSelectedLetters] = useState([]);
  const [positions, setPositions] = useState(PREDEFINED_DESIGNATIONS);
  const [posOpen, setPosOpen] = useState(false);
  const [posSearch, setPosSearch] = useState('');
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const [natOpen, setNatOpen] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    employee_name: '',
    nationality: '',
    passport_no: '',
    phone_no: '',
    signatory_index: 0,
    annexure_a: { ...defaultAnnexureA },
    annexure_b: { ...defaultAnnexureB },
  });

  useEffect(() => {
    fetchLetters();
    fetchGeographies();
    fetchPositions();
  }, []);

  const fetchPositions = async () => {
    try {
      const r = await axios.get(`${API}/api/positions`);
      setPositions(r.data || []);
    } catch (e) { /* keep predefined */ }
  };

  const fetchLetters = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/offer-letters`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLetters(response.data);
    } catch (error) {
      console.error('Error fetching offer letters:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (letter, status) => {
    if ((letter.status || 'pending') === status) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.patch(`${API}/api/offer-letters/${letter.id}/status`, { status },
        { headers: { Authorization: `Bearer ${token}` } });
      setLetters(prev => prev.map(l => (l.id === letter.id ? { ...l, status } : l)));
      const ob = res.data?.onboarding;
      toast({
        title: status === 'refused' ? 'Marked as Refused' : status === 'signed' ? 'Marked as Signed' : 'Marked as Pending',
        description: `${letter.employee_name} — offer ${status}.${ob === 'created' ? ' Onboarding card created (Offer Accepted).' : ob === 'deleted' ? ' Onboarding card removed.' : ''}`,
      });
    } catch (error) {
      toast({ title: 'Error', description: 'Could not update status', variant: 'destructive' });
    }
  };


  const fetchGeographies = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/offer-letters/geographies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGeographies(response.data);
    } catch (error) {
      console.error('Error fetching geographies:', error);
    }
  };

  const handleAddGeography = async () => {
    if (!newGeography.trim()) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/offer-letters/geographies?geography=${encodeURIComponent(newGeography)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGeographies([...geographies, newGeography]);
      setNewGeography('');
      toast({ title: 'Success', description: 'Geography added successfully' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to add geography', variant: 'destructive' });
    }
  };

  const handleSubmit = async () => {
    if (!formData.employee_name.trim()) {
      toast({ title: 'Error', description: 'Employee name is required', variant: 'destructive' });
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (editingLetter) {
        await axios.put(`${API}/api/offer-letters/${editingLetter.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({ title: 'Success', description: 'Offer letter updated successfully' });
      } else {
        await axios.post(`${API}/api/offer-letters`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({ title: 'Success', description: 'Offer letter created successfully' });
      }
      fetchLetters();
      resetForm();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to save offer letter', variant: 'destructive' });
    }
  };

  const handleEdit = (letter) => {
    setExpandedSection('basic');
    setFormData({
      employee_name: letter.employee_name || '',
      nationality: letter.nationality || '',
      passport_no: letter.passport_no || '',
      phone_no: letter.phone_no || '',
      department: letter.department || '',
      company: letter.company || '',
      signatory_index: SIGNATORIES.findIndex(s => s.name === letter.signatory?.name) || 0,
      annexure_a: {
        ...defaultAnnexureA,
        ...letter.annexure_a,
        // Backward compatibility: map old 'allowances' to 'housing_allowance'
        housing_allowance: letter.annexure_a?.housing_allowance || letter.annexure_a?.allowances || '',
      },
      annexure_b: { ...defaultAnnexureB, ...letter.annexure_b },
    });
    setEditingLetter(letter);
    setShowForm(true);
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/offer-letters/${deleteConfirm.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Success', description: 'Offer letter deleted successfully' });
      fetchLetters();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete offer letter', variant: 'destructive' });
    }
    setDeleteConfirm(null);
  };

  const handleDownload = async (letter, format) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/offer-letters/${letter.id}/download?format=${format}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `OfferLetter_${letter.employee_name.replace(/\s+/g, '_')}.${format === 'word' ? 'docx' : 'pdf'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast({ title: 'Success', description: `Offer letter downloaded as ${format.toUpperCase()}` });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to download offer letter', variant: 'destructive' });
    }
  };

  // Preview PDF function
  const handlePreview = async (letter) => {
    setPreviewLetter(letter);
    setPreviewLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/offer-letters/${letter.id}/download?format=pdf`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      setPreviewUrl(url);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load preview', variant: 'destructive' });
      setPreviewLetter(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
    }
    setPreviewLetter(null);
    setPreviewUrl('');
  };

  // Bulk selection functions
  const toggleSelectLetter = (letterId) => {
    setSelectedLetters(prev => 
      prev.includes(letterId) 
        ? prev.filter(id => id !== letterId)
        : [...prev, letterId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedLetters.length === letters.length) {
      setSelectedLetters([]);
    } else {
      setSelectedLetters(letters.map(l => l.id));
    }
  };

  const handleBulkDownload = async () => {
    if (selectedLetters.length === 0) {
      toast({ title: 'Info', description: 'Please select at least one offer letter' });
      return;
    }

    setBulkDownloading(true);
    const token = localStorage.getItem('token');
    let successCount = 0;
    let errorCount = 0;

    for (const letterId of selectedLetters) {
      const letter = letters.find(l => l.id === letterId);
      if (!letter) continue;

      try {
        const response = await axios.get(
          `${API}/api/offer-letters/${letterId}/download?format=pdf`,
          {
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob'
          }
        );
        
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `OfferLetter_${letter.employee_name.replace(/\s+/g, '_')}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        successCount++;
        
        // Small delay between downloads to prevent browser blocking
        await new Promise(resolve => setTimeout(resolve, 300));
      } catch (error) {
        errorCount++;
      }
    }

    setBulkDownloading(false);
    setSelectedLetters([]);
    
    if (errorCount === 0) {
      toast({ title: 'Success', description: `Downloaded ${successCount} offer letter(s)` });
    } else {
      toast({ 
        title: 'Partial Success', 
        description: `Downloaded ${successCount}, failed ${errorCount}`,
        variant: errorCount > successCount ? 'destructive' : 'default'
      });
    }
  };

  const resetForm = () => {
    setFormData({
      employee_name: '',
      nationality: '',
      passport_no: '',
      phone_no: '',
      department: '',
      company: '',
      signatory_index: 0,
      annexure_a: { ...defaultAnnexureA },
      annexure_b: { ...defaultAnnexureB },
    });
    setEditingLetter(null);
    setShowForm(false);
  };

  const updateAnnexureA = (field, value) => {
    setFormData(prev => {
      const newAnnexureA = { ...prev.annexure_a, [field]: value };

      // Enter the Total Wage → auto-split into Basic 60% / Housing 25% /
      // Transportation 10% / Others (remainder ~5%). Whole numbers, no decimals.
      if (field === 'total_wage') {
        const total = Math.round(parseFloat(String(value).replace(/[^0-9.]/g, '') || '0')) || 0;
        if (total > 0) {
          const basic = Math.round(total * 0.60);
          const housing = Math.round(total * 0.25);
          const transportation = Math.round(total * 0.10);
          const others = total - basic - housing - transportation;
          newAnnexureA.basic_wage = String(basic);
          newAnnexureA.housing_allowance = String(housing);
          newAnnexureA.transportation_allowance = String(transportation);
          newAnnexureA.others = String(others);
        } else {
          newAnnexureA.basic_wage = '';
          newAnnexureA.housing_allowance = '';
          newAnnexureA.transportation_allowance = '';
          newAnnexureA.others = '';
        }
      }

      return {
        ...prev,
        annexure_a: newAnnexureA
      };
    });
  };

  const updateAnnexureB = (field, value) => {
    setFormData(prev => ({
      ...prev,
      annexure_b: { ...prev.annexure_b, [field]: value }
    }));
  };

  const toggleGeography = (geo) => {
    const current = formData.annexure_b.non_compete_geography || [];
    if (current.includes(geo)) {
      updateAnnexureB('non_compete_geography', current.filter(g => g !== geo));
    } else {
      updateAnnexureB('non_compete_geography', [...current, geo]);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Offer Letters</h1>
          <p className="text-gray-500 mt-1">Create and manage employment offer letters</p>
        </div>
        {!isViewer && (
          <Button onClick={() => setShowForm(true)} data-testid="create-offer-letter-btn">
            <Plus className="w-4 h-4 mr-2" />
            Create Offer Letter
          </Button>
        )}
      </div>

      {/* Offer Letters History */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Offer Letter History ({letters.length})
            </CardTitle>
            {letters.length > 0 && (
              <div className="flex items-center gap-3">
                <button
                  onClick={toggleSelectAll}
                  className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
                  data-testid="select-all-btn"
                >
                  {selectedLetters.length === letters.length ? (
                    <CheckSquare className="w-4 h-4 text-blue-600" />
                  ) : (
                    <Square className="w-4 h-4" />
                  )}
                  {selectedLetters.length === letters.length ? 'Deselect All' : 'Select All'}
                </button>
                {selectedLetters.length > 0 && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleBulkDownload}
                    disabled={bulkDownloading}
                    data-testid="bulk-download-btn"
                  >
                    <Download className="w-4 h-4 mr-1" />
                    {bulkDownloading ? 'Downloading...' : `Download ${selectedLetters.length} PDF(s)`}
                  </Button>
                )}
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {letters.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No offer letters created yet</p>
              <p className="text-sm">Click "Create Offer Letter" to get started</p>
            </div>
          ) : (
            <div className="space-y-4">
              {letters.map((letter) => (
                <div key={letter.id} className={`flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 ${letter.status === 'refused' ? 'bg-red-50 border-red-200' : letter.status === 'signed' ? 'border-green-200' : ''}`} data-testid={`offer-letter-${letter.id}`}>
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={selectedLetters.includes(letter.id)}
                      onCheckedChange={() => toggleSelectLetter(letter.id)}
                      data-testid={`select-${letter.id}`}
                    />
                    <div className="flex-1">
                      <h3
                        className={`font-semibold ${letter.status === 'refused' ? 'text-red-600 line-through' : 'text-gray-900'}`}
                        data-testid={`offer-name-${letter.id}`}
                      >
                        {letter.employee_name}
                      </h3>
                      <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                        <span>Nationality: {letter.nationality}</span>
                        {letter.department && <span data-testid={`offer-dept-${letter.id}`}>Dept: {letter.department}</span>}
                        {letter.company && <span data-testid={`offer-company-${letter.id}`}>Company: {letter.company}</span>}
                        <span>Position: {letter.annexure_a?.position || 'N/A'}</span>
                        <Badge variant="outline">{letter.signatory?.name}</Badge>
                        <StatusBadge status={letter.status} testid={`offer-status-badge-${letter.id}`} />
                      </div>
                      <p className="text-xs text-gray-400 mt-1">
                        {letter.serial_no && (
                          <span className="font-mono font-semibold text-rose-600 mr-2" data-testid={`offer-serial-${letter.id}`}>
                            {letter.serial_no}
                          </span>
                        )}
                        Created: {new Date(letter.created_at).toLocaleDateString('en-GB')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {!isViewer && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="sm" data-testid={`offer-status-btn-${letter.id}`}>
                            Status: {statusLabel(letter.status)}
                            <ChevronDown className="w-3.5 h-3.5 ml-1" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => updateStatus(letter, 'pending')} data-testid={`offer-status-pending-${letter.id}`}>
                            <Square className="w-4 h-4 mr-2 text-gray-500" /> Pending
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => updateStatus(letter, 'signed')} data-testid={`offer-status-signed-${letter.id}`}>
                            <Check className="w-4 h-4 mr-2 text-green-600" /> Candidate Signed
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => updateStatus(letter, 'refused')} data-testid={`offer-status-refused-${letter.id}`}>
                            <X className="w-4 h-4 mr-2 text-red-600" /> Candidate Refused
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                    <Button variant="outline" size="sm" onClick={() => handlePreview(letter)} data-testid={`preview-${letter.id}`}>
                      <Eye className="w-4 h-4 mr-1" />
                      Preview
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleDownload(letter, 'pdf')} data-testid={`download-pdf-${letter.id}`}>
                      <FileDown className="w-4 h-4 mr-1" />
                      PDF
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleDownload(letter, 'word')} data-testid={`download-word-${letter.id}`}>
                      <Download className="w-4 h-4 mr-1" />
                      Word
                    </Button>
                    <SendReportButton
                      reportUrl={`/api/offer-letters/${letter.id}/download?format=pdf`}
                      filename={`OfferLetter_${(letter.employee_name || '').replace(/\s+/g, '_')}.pdf`}
                      defaultSubject={`Offer Letter — ${letter.employee_name || ''}`}
                      size="sm"
                      className="border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                      testid={`email-offer-${letter.id}`}
                    />
                    {!isViewer && (
                      <>
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(letter)} data-testid={`edit-${letter.id}`}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" onClick={() => setDeleteConfirm(letter)} data-testid={`delete-${letter.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Form Dialog */}
      <Dialog open={showForm} onOpenChange={() => resetForm()}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingLetter ? 'Edit Offer Letter' : 'Create New Offer Letter'}</DialogTitle>
            <DialogDescription>Fill in the employee details and contract information</DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Basic Information */}
            <div className="border rounded-lg">
              <button 
                className="w-full p-4 flex justify-between items-center text-left font-semibold"
                onClick={() => setExpandedSection(expandedSection === 'basic' ? '' : 'basic')}
              >
                <span>Basic Information</span>
                {expandedSection === 'basic' ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </button>
              {expandedSection === 'basic' && (
                <div className="p-4 border-t space-y-4">
                  <OfferDeptCompanyFields formData={formData} setFormData={setFormData} />
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Employee Name *</Label>
                      <Input 
                        value={formData.employee_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, employee_name: e.target.value }))}
                        placeholder="Enter employee name"
                        data-testid="employee-name-input"
                      />
                    </div>
                    <div>
                      <Label>Nationality *</Label>
                      <Popover open={natOpen} onOpenChange={setNatOpen}>
                        <PopoverTrigger asChild>
                          <Button variant="outline" role="combobox" aria-expanded={natOpen}
                            className="w-full justify-between font-normal"
                            data-testid="nationality-input">
                            {formData.nationality || "Select nationality..."}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-full p-0" align="start">
                          <Command>
                            <CommandInput placeholder="Search nationality..." />
                            <CommandList>
                              <CommandEmpty>No nationality found.</CommandEmpty>
                              <CommandGroup heading="Common">
                                {TOP_NATIONALITIES.map(nat => (
                                  <CommandItem key={nat} value={`${nat} ${NATIONALITY_ALIASES[nat] || ''}`} onSelect={() => { setFormData(prev => ({ ...prev, nationality: nat })); setNatOpen(false); }}>
                                    <Check className={`mr-2 h-4 w-4 ${formData.nationality === nat ? 'opacity-100' : 'opacity-0'}`} />
                                    {nat}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                              <CommandGroup heading="All">
                                {ALL_NATIONALITIES.filter(n => !TOP_NATIONALITIES.includes(n)).map(nat => (
                                  <CommandItem key={nat} value={`${nat} ${NATIONALITY_ALIASES[nat] || ''}`} onSelect={() => { setFormData(prev => ({ ...prev, nationality: nat })); setNatOpen(false); }}>
                                    <Check className={`mr-2 h-4 w-4 ${formData.nationality === nat ? 'opacity-100' : 'opacity-0'}`} />
                                    {nat}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Passport No. <span className="text-gray-400 font-normal">(optional)</span></Label>
                      <Input
                        value={formData.passport_no}
                        onChange={(e) => setFormData(prev => ({ ...prev, passport_no: e.target.value }))}
                        placeholder="Enter passport number"
                        data-testid="passport-no-input"
                      />
                    </div>
                    <div>
                      <Label>Phone No. <span className="text-gray-400 font-normal">(optional)</span></Label>
                      <Input
                        value={formData.phone_no}
                        onChange={(e) => setFormData(prev => ({ ...prev, phone_no: e.target.value }))}
                        placeholder="Enter phone number"
                        data-testid="phone-no-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Contract Signatory</Label>
                    <Select 
                      value={String(formData.signatory_index)}
                      onValueChange={(v) => setFormData(prev => ({ ...prev, signatory_index: parseInt(v) }))}
                    >
                      <SelectTrigger data-testid="signatory-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SIGNATORIES.map((sig, index) => (
                          <SelectItem key={index} value={String(index)}>
                            {sig.name} - {sig.designation}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>

            {/* Annexure A - Particulars */}
            <div className="border rounded-lg">
              <button 
                className="w-full p-4 flex justify-between items-center text-left font-semibold"
                onClick={() => setExpandedSection(expandedSection === 'annexure_a' ? '' : 'annexure_a')}
              >
                <span>Annexure A - Particulars of Employment</span>
                {expandedSection === 'annexure_a' ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </button>
              {expandedSection === 'annexure_a' && (
                <div className="p-4 border-t space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>1. Position *</Label>
                      <Popover open={posOpen} onOpenChange={setPosOpen}>
                        <PopoverTrigger asChild>
                          <Button variant="outline" role="combobox" className="w-full justify-between font-normal" data-testid="position-input">
                            <span className={formData.annexure_a.position ? '' : 'text-gray-400'}>
                              {formData.annexure_a.position || 'Select or type a position'}
                            </span>
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                          <Command>
                            <CommandInput placeholder="Search position..." value={posSearch} onValueChange={setPosSearch} />
                            <CommandList>
                              <CommandEmpty>No match — type to add a new one.</CommandEmpty>
                              {posSearch.trim() && !positions.some(p => p.toLowerCase() === posSearch.trim().toLowerCase()) && (
                                <CommandItem value={`__new__${posSearch}`} onSelect={() => { const np = posSearch.trim().toUpperCase(); updateAnnexureA('position', np); setPositions(prev => [...prev, np]); axios.post(`${API}/api/positions`, { position: np }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }).catch(() => {}); setPosOpen(false); setPosSearch(''); }} data-testid="position-add-new">
                                  <Plus className="mr-2 h-4 w-4" /> Add "{posSearch.trim().toUpperCase()}"
                                </CommandItem>
                              )}
                              <CommandGroup>
                                {positions.map((pos) => (
                                  <CommandItem key={pos} value={pos} onSelect={() => { updateAnnexureA('position', pos); setPosOpen(false); setPosSearch(''); }}>
                                    <Check className={`mr-2 h-4 w-4 ${formData.annexure_a.position === pos ? 'opacity-100' : 'opacity-0'}`} />
                                    {pos}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div>
                      <Label>2. Term of Contract</Label>
                      <Input 
                        value={formData.annexure_a.term_of_contract}
                        onChange={(e) => updateAnnexureA('term_of_contract', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>3. Starting Date</Label>
                      <Input 
                        type="date"
                        value={formData.annexure_a.starting_date}
                        onChange={(e) => updateAnnexureA('starting_date', e.target.value)}
                        data-testid="starting-date-input"
                      />
                    </div>
                    <div>
                      <Label>4. Total Wage (AED) * — auto-splits below</Label>
                      <Input 
                        value={formData.annexure_a.total_wage}
                        onChange={(e) => updateAnnexureA('total_wage', e.target.value)}
                        placeholder="e.g., 4500"
                        data-testid="total-wage-input"
                      />
                      <p className="text-xs text-gray-500 mt-1">Basic 60% · Housing 25% · Transportation 10% · Others 5% (rounded)</p>
                    </div>
                    <div>
                      <Label>5. Basic Wage (AED) — auto</Label>
                      <Input 
                        value={formData.annexure_a.basic_wage}
                        readOnly
                        className="bg-gray-100"
                        placeholder="60% of total"
                        data-testid="basic-wage-input"
                      />
                    </div>
                    <div>
                      <Label>6. Housing Allowance (AED) — auto</Label>
                      <Input 
                        value={formData.annexure_a.housing_allowance}
                        readOnly
                        className="bg-gray-100"
                        placeholder="25% of total"
                        data-testid="housing-allowance-input"
                      />
                    </div>
                    <div>
                      <Label>7. Transportation Allowance (AED) — auto</Label>
                      <Input 
                        value={formData.annexure_a.transportation_allowance}
                        readOnly
                        className="bg-gray-100"
                        placeholder="10% of total"
                        data-testid="transportation-allowance-input"
                      />
                    </div>
                    <div>
                      <Label>8. Others (AED) — auto</Label>
                      <Input 
                        value={formData.annexure_a.others}
                        readOnly
                        className="bg-gray-100"
                        placeholder="remainder (~5%)"
                        data-testid="others-input"
                      />
                    </div>
                    <div>
                      <Label>9. Benefits-in-kind</Label>
                      <Input 
                        value={formData.annexure_a.benefits_in_kind}
                        onChange={(e) => updateAnnexureA('benefits_in_kind', e.target.value)}
                      />
                    </div>
                    <div className="col-span-2">
                      <Label>10. Ticket Policy</Label>
                      <Select 
                        value={formData.annexure_a.ticket_policy || '730'}
                        onValueChange={(v) => updateAnnexureA('ticket_policy', v)}
                      >
                        <SelectTrigger data-testid="ticket-policy-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="730">Every 730 days (2 years) - Economy class air ticket</SelectItem>
                          <SelectItem value="365">Every 365 days (1 year) - Economy class air ticket</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-500 mt-1">
                        Air ticket to point of hire from Abu Dhabi, subject to company policy
                      </p>
                    </div>
                    <div>
                      <Label>11. Description of Duties</Label>
                      <Input 
                        value={formData.annexure_a.description_of_duties}
                        onChange={(e) => updateAnnexureA('description_of_duties', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>12. Primary Location</Label>
                      <Input 
                        value={formData.annexure_a.primary_location}
                        onChange={(e) => updateAnnexureA('primary_location', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>13. Probation Period</Label>
                      <Input 
                        value={formData.annexure_a.probation_period}
                        onChange={(e) => updateAnnexureA('probation_period', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>14. Normal Hours</Label>
                      <Input 
                        value={formData.annexure_a.normal_hours}
                        onChange={(e) => updateAnnexureA('normal_hours', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>15. Sick Days</Label>
                      <Input 
                        value={formData.annexure_a.sick_days}
                        onChange={(e) => updateAnnexureA('sick_days', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>16. Paid Annual Leave</Label>
                      <Input 
                        value={formData.annexure_a.paid_annual_leave}
                        onChange={(e) => updateAnnexureA('paid_annual_leave', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>17. Resignation & Termination Notice</Label>
                      <Input 
                        value={formData.annexure_a.resignation_notice}
                        onChange={(e) => updateAnnexureA('resignation_notice', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>18. Special Conditions</Label>
                      <Textarea 
                        value={formData.annexure_a.special_conditions}
                        onChange={(e) => updateAnnexureA('special_conditions', e.target.value)}
                        placeholder="Enter any special conditions"
                        rows={2}
                      />
                    </div>
                    <div>
                      <Label>19. Additional Conditions</Label>
                      <Input 
                        value={formData.annexure_a.additional_conditions}
                        onChange={(e) => updateAnnexureA('additional_conditions', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>20. Language Requirement</Label>
                      <Input 
                        value={formData.annexure_a.language_requirement}
                        onChange={(e) => updateAnnexureA('language_requirement', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>21. Personal Email</Label>
                      <Input 
                        value={formData.annexure_a.personal_email}
                        onChange={(e) => updateAnnexureA('personal_email', e.target.value)}
                        placeholder="employee@personal.com"
                        data-testid="personal-email-input"
                      />
                    </div>
                    <div>
                      <Label>22. WhatsApp Contact</Label>
                      <Input 
                        value={formData.annexure_a.whatsapp_contact}
                        onChange={(e) => updateAnnexureA('whatsapp_contact', e.target.value)}
                        placeholder="+971 XX XXX XXXX"
                        data-testid="whatsapp-input"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Annexure B - Non-Compete */}
            <div className="border rounded-lg">
              <button 
                className="w-full p-4 flex justify-between items-center text-left font-semibold"
                onClick={() => setExpandedSection(expandedSection === 'annexure_b' ? '' : 'annexure_b')}
              >
                <span>Annexure B - Non-Compete Agreement</span>
                {expandedSection === 'annexure_b' ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </button>
              {expandedSection === 'annexure_b' && (
                <div className="p-4 border-t space-y-4">
                  <div>
                    <Label>Non-Compete Duration</Label>
                    <Input 
                      value={formData.annexure_b.non_compete_duration}
                      onChange={(e) => updateAnnexureB('non_compete_duration', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Non-Compete Geography</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {geographies.map((geo) => (
                        <Badge 
                          key={geo}
                          variant={formData.annexure_b.non_compete_geography?.includes(geo) ? 'default' : 'outline'}
                          className="cursor-pointer"
                          onClick={() => toggleGeography(geo)}
                        >
                          {geo}
                          {formData.annexure_b.non_compete_geography?.includes(geo) && (
                            <X className="w-3 h-3 ml-1" />
                          )}
                        </Badge>
                      ))}
                    </div>
                    <div className="flex gap-2 mt-3">
                      <Input 
                        value={newGeography}
                        onChange={(e) => setNewGeography(e.target.value)}
                        placeholder="Add new geography"
                        className="flex-1"
                      />
                      <Button variant="outline" onClick={handleAddGeography}>
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <div>
                    <Label>Type of Work *</Label>
                    <Input 
                      value={formData.annexure_b.type_of_work}
                      onChange={(e) => updateAnnexureB('type_of_work', e.target.value)}
                      placeholder="e.g., Restaurant Operations, Finance"
                      data-testid="type-of-work-input"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={resetForm}>Cancel</Button>
            <Button onClick={handleSubmit} data-testid="save-offer-letter-btn">
              {editingLetter ? 'Update Offer Letter' : 'Create Offer Letter'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Offer Letter</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the offer letter for "{deleteConfirm?.employee_name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* PDF Preview Dialog */}
      <Dialog open={!!previewLetter} onOpenChange={() => closePreview()}>
        <DialogContent className="max-w-5xl max-h-[95vh] p-0">
          <DialogHeader className="p-4 border-b">
            <DialogTitle className="flex items-center justify-between">
              <span>Preview: {previewLetter?.employee_name}</span>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => previewLetter && handleDownload(previewLetter, 'pdf')}
                  data-testid="preview-download-pdf"
                >
                  <FileDown className="w-4 h-4 mr-1" />
                  Download PDF
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => previewLetter && handleDownload(previewLetter, 'word')}
                  data-testid="preview-download-word"
                >
                  <Download className="w-4 h-4 mr-1" />
                  Download Word
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 bg-gray-100 overflow-hidden" style={{ height: 'calc(95vh - 80px)' }}>
            {previewLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
              </div>
            ) : previewUrl ? (
              <iframe
                src={previewUrl}
                title="PDF Preview"
                className="w-full h-full"
                style={{ border: 'none' }}
              />
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                Unable to load preview
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
